import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import {
  ToggleComponent,
  ToggleSize,
} from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { firstValueFrom } from "rxjs";
import { userContextStore } from "../../../../shared/data/user-context.store";
import { piaCatalogStore } from "../../data/pia-catalog.store";
import { AppHeaderComponent } from "../../shared/app-header/app-header";
import { DocumentSettingsModalButton } from "../../shared/components/document-settings-modal/document-settings-modal-button/document-settings-modal-button";
import { EmptyFavoritesList } from "../../shared/components/empty-favorites-list/empty-favorites-list";
import { NotFoundComponent } from "../../shared/components/not-found/not-found";
import { pathRouteDataHelper$ } from "../../shared/helpers/path-params.helper";
import { ProductSeriesClusteredByFamily } from "../../shared/models/product-series-clustered-by-family";
import { DefaultProduct } from "../../shared/pia-api/generated";
import { PiaLocaleService } from "../../shared/services/pia-locale.service";
import { ProductFamilyFavoritesService } from "../../shared/services/product-family-favorites.service";
import { ProductFamilyFilterService } from "../../shared/services/product-family-filter.service";
import { ShowOnlyFavoritesService } from "../../shared/services/show-only-favorites.service";
import { ProductFamilyAside } from "../product-family/product-family-aside/product-family-aside";
import { ProductFamilyProductsList } from "../product-family/product-family-products-list/product-family-products-list";
import { ProductFamilyYearSelect } from "../product-family/product-family-year-select/product-family-year-select";

@Component({
  selector: "claas-product-overview",
  imports: [
    NotFoundComponent,
    TranslatePipe,
    AppHeaderComponent,
    ProductFamilyAside,
    ProductFamilyYearSelect,
    ProductFamilyProductsList,
    EmptyFavoritesList,
    DocumentSettingsModalButton,
    ToggleComponent,
  ],
  templateUrl: "./product-overview.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-overview.scss",
})
export class ProductOverviewComponent {
  // Services
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private userContext = inject(userContextStore);
  private piaLocale = inject(PiaLocaleService);
  private catalog = inject(piaCatalogStore);
  private productFamilyFilterService = inject(ProductFamilyFilterService);
  private productFavoriteFamilyService = inject(ProductFamilyFavoritesService);
  private showOnlyFavoritesService = inject(ShowOnlyFavoritesService);

  // Last country / language applied from the profile selection, used to detect
  // changes and avoid re-triggering navigation on our own updates.
  private lastCountry = this.userContext.country();
  private lastLanguage = this.userContext.language();

  // Constants
  protected readonly toggleSize = ToggleSize;

  // Signals for state management
  favoriteIds = toSignal(this.productFavoriteFamilyService.includedIds$);
  data = toSignal(pathRouteDataHelper$());
  onlyFavoritesStatus = toSignal(
    this.showOnlyFavoritesService.showOnlyFavoritesStatus$,
  );
  excludedIds = toSignal(this.productFamilyFilterService.excludedIds$);

  // Computed properties
  isChecked = computed(() => {
    return this.onlyFavoritesStatus() as boolean;
  });

  isInternationalCatalog = computed(() => this.catalog.international());

  productFamiliesFiltered = computed(() => {
    const families = this.data()?.["productFamilies"] as DefaultProduct[];
    const excluded = this.excludedIds() ?? [];
    return (
      families?.filter(
        (productFamily: DefaultProduct) =>
          productFamily.ID && !excluded.includes(productFamily.ID),
      ) ?? []
    );
  });

  years = computed(() => this.data()?.["years"] as number[]);

  defaultYear = computed(() => this.data()?.["selectedYear"] as number);

  productSeries = computed(() => {
    const productSeriesCurrent = this.data()?.["productSeries"];
    if (!productSeriesCurrent) {
      return productSeriesCurrent;
    }

    const productSeriesFilteredByFavorites =
      {} as ProductSeriesClusteredByFamily;

    for (const key in productSeriesCurrent) {
      if (this.onlyFavoritesStatus()) {
        // Filter out all items that are not marked as favorites
        const favoriteProductSeries = productSeriesCurrent[key].filter(
          (productSerie: DefaultProduct) => {
            return this.favoriteIds()?.includes(productSerie.ID as string);
          },
        );

        if (favoriteProductSeries.length) {
          productSeriesFilteredByFavorites[key] = favoriteProductSeries;
        }
      } else {
        // Leave the object as it is, favorites are ignored
        productSeriesFilteredByFavorites[key] = productSeriesCurrent[key];
      }
    }

    return productSeriesFilteredByFavorites;
  });

  requiredData = computed(
    () => this.years()?.length && this.data()?.["productFamilies"]?.length,
  );

  constructor() {
    // Country and language are picked once, in the poff profile menu, and land
    // in the shared UserContextStore. React to those changes here to reconcile
    // PIA's product URL. Reconciling never writes back to the profile
    // selection.
    effect(() => {
      const country = this.userContext.country();
      const language = this.userContext.language();
      if (country === this.lastCountry && language === this.lastLanguage) {
        return;
      }
      this.lastCountry = country;
      this.lastLanguage = language;
      void this.reconcileProductLocale();
    });
  }

  // Event Handlers
  handleToggleFavouritesChange(isChecked: boolean): void {
    this.showOnlyFavoritesService.setOnlyFavoritesStatus(isChecked);
  }

  /**
   * Switches between the catalogue of the profile country and PIA's
   * international one. Only the PIA product URL changes — the profile
   * selection stays untouched, because `INT` is not a country the poff
   * backends know.
   */
  handleToggleInternationalCatalog(isChecked: boolean): void {
    this.catalog.setInternational(isChecked);
    void this.reconcileProductLocale();
  }

  /**
   * Points PIA's product URL at the locale the profile selection resolves to.
   * Both axes are negotiated silently by `PiaLocaleService`: the profile
   * language is kept whenever PIA offers it for the resolved country,
   * otherwise PIA's own best match — and finally the configured default — is
   * used. The user is never asked again: the profile menu is the single place
   * where country and language are picked.
   */
  private async reconcileProductLocale(): Promise<void> {
    const { countryID, languageID } = await firstValueFrom(
      this.piaLocale.resolveLocale(),
    );
    this.navigateToProductInfo(countryID, languageID);
  }

  private navigateToProductInfo(country: string, language: string): void {
    this.router.navigate([
      "pia",
      country.toLowerCase(),
      language.toLowerCase(),
      "product-info",
    ]);
  }
}
